import warning from '../tiny-warning';
import {
  Location,
  Action,
  LocationListener,
  LocationState,
  UnregisterCallback,
} from './index';
import { getConfirmation } from './DOMUtils';

export type PromptFunction<S = LocationState> = (
  location: Location<S>,
  action: Action
) => any;

export type Prompt<S = LocationState> = PromptFunction<S> | string | boolean;

export interface TransitionManager<S = LocationState> {
  setPrompt(nextPrompt?: Prompt<S>): UnregisterCallback;
  appendListener(listener: LocationListener<S>): UnregisterCallback;
  notifyListeners(location: Location<S>, action: Action): void;
  confirmTransitionTo(
    location: Location<S>,
    action: Action,
    getUserConfirmation: typeof getConfirmation | undefined,
    callback: (result: boolean) => void
  ): void;
}

function createTransitionManager<S = LocationState>(): TransitionManager<S> {
  let prompt: Prompt<S> | null = null;

  function setPrompt(nextPrompt?: Prompt<S>): UnregisterCallback {
    warning(prompt == null, 'A history supports only one prompt at a time');

    prompt = nextPrompt ?? null;

    return () => {
      if (prompt === nextPrompt) prompt = null;
    };
  }

  function confirmTransitionTo(
    location: Location<S>,
    action: Action,
    getUserConfirmation: typeof getConfirmation | undefined,
    callback: (ok: boolean) => void
  ): void {
    // TODO: If another transition starts while we're still confirming
    // the previous one, we may end up in a weird state. Figure out the
    // best way to handle this.
    if (prompt != null) {
      const result =
        typeof prompt === 'function' ? prompt(location, action) : prompt;

      if (typeof result === 'string') {
        if (typeof getUserConfirmation === 'function') {
          getUserConfirmation(result, callback);
        } else {
          warning(
            false,
            'A history needs a getUserConfirmation function in order to use a prompt message'
          );

          callback(true);
        }
      } else {
        // Return false from a transition hook to cancel the transition.
        callback(result !== false);
      }
    } else {
      callback(true);
    }
  }

  let listeners: LocationListener<S>[] = [];

  function appendListener(fn: LocationListener<S>): UnregisterCallback {
    let isActive = true;

    function listener(...args: [Location<S>, Action]) {
      if (isActive) fn(...args);
    }

    listeners.push(listener);

    return () => {
      isActive = false;
      listeners = listeners.filter((item) => item !== listener);
    };
  }

  function notifyListeners(location: Location<S>, action: Action): void {
    listeners.forEach((listener) => listener(location, action));
  }

  return {
    setPrompt,
    confirmTransitionTo,
    appendListener,
    notifyListeners,
  };
}

export default createTransitionManager;
